// @flow

import styles from './SavedJobsDashboard.less';
import commonStyles from '../SignedInDashboardCommon.less';

import React, { Component } from 'react';
import { connect } from 'react-redux';

import { locationFromHref } from '@seek/seek-jobs-seo/build/location';
import {
  ScreenReaderOnly,
  TextLink,
  StarIcon,
  Text
} from 'seek-style-guide/react';
import DashboardHeading from '../DashboardHeading/DashboardHeading';
import DashboardItem from '../DashboardItem/DashboardItem';
import DashboardSectionError from '../DashboardSectionError/DashboardSectionError';

import NavLink from 'src/components/NavLink/NavLink';
import ViewAllLink from '../ViewAllLink/ViewAllLink';

import { getDashboard } from 'src/store/dashboard/dashboard';

import { get, isUndefined } from 'lodash';

type SavedJob = {
  id: number,
  title: string,
  advertiser: string,
  isExpired: boolean
};

type Props = {
  savedJobs?: Array<SavedJob>,
  totalCount?: number,
  displayTotalCount?: string,
  retrySavedJobs: Function
};

const mapDispatchToProps = dispatch => ({
  retrySavedJobs: () => dispatch(getDashboard({ sections: 'savedJobs' }))
});

const mapStateToProps = ({ dashboard: { savedJobs } }) => ({
  totalCount: get(savedJobs, 'totalCount'),
  savedJobs: get(savedJobs, 'data'),
  displayTotalCount: get(savedJobs, 'displayTotalCount')
});

class SavedJobsDashboard extends Component<Props> {
  props: Props;

  renderJobItem = savedJob => {
    const { id, title, advertiser, isExpired } = savedJob;

    /* eslint-disable react/jsx-no-bind */
    return (
      <DashboardItem
        key={id}
        heading={() => (
          <span>
            {isExpired && (
              <span data-automation="savedJobExpired">
                Expired<ScreenReaderOnly>job</ScreenReaderOnly>
                <span>{' — '}</span>
              </span>
            )}
            <NavLink
              component={TextLink}
              children={
                <span>
                  {title}
                  <ScreenReaderOnly>. At {advertiser}</ScreenReaderOnly>
                </span>
              }
              analytics="dashboard-save-job-click"
              data-automation="savedJobHeadingLink"
              location={locationFromHref(
                `/${isExpired ? 'expiredjob' : 'job'}/${id}`
              )}
            />
          </span>
        )}
        subheading={() => (
          <span aria-hidden="true" data-automation="savedJobAdvertiser">
            {advertiser}
          </span>
        )}
      />
    );
    /* eslint-enable react/jsx-no-bind */
  };

  render() {
    const {
      savedJobs,
      totalCount,
      displayTotalCount,
      retrySavedJobs
    } = this.props;
    const label = `Saved job${totalCount === 1 ? '' : 's'}`;

    const viewAllLinkProps = {
      analytics: 'dashboard-all-saved-jobs-click',
      location: '/my-activity/saved-jobs'
    };

    return (
      <nav
        aria-label={label}
        role="navigation"
        data-automation="savedJobsDashboard"
      >
        <DashboardHeading
          label={label}
          count={displayTotalCount}
          Icon={StarIcon}
          iconClass={styles.icon}
          className={styles.heading}
          href="/my-activity/saved-jobs"
          analytics="dashboard-heading-saved-jobs-click"
        />
        {isUndefined(savedJobs) ? (
          <DashboardSectionError
            dataAutomation="savedJobsSectionError"
            label={label}
            navLinkProps={viewAllLinkProps}
            retryHandler={retrySavedJobs}
          />
        ) : (
          <div>
            {savedJobs && savedJobs.length > 0 ? (
              <div className={commonStyles.items}>
                {savedJobs.map(this.renderJobItem)}
                <ViewAllLink
                  {...viewAllLinkProps}
                  dataAutomation="savedJobViewSavedJobs"
                  screenReaderText="your saved jobs"
                />
              </div>
            ) : (
              <Text secondary small className={commonStyles.noItems}>
                Use the&nbsp;
                <span className={styles.noItems_highlight}>
                  <StarIcon svgClassName={styles.iconSvg} />
                  &nbsp;Save&nbsp;
                </span>
                button on each job listing to save it for later. You can then
                access them on all your devices.
              </Text>
            )}
          </div>
        )}
      </nav>
    );
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(SavedJobsDashboard);
